import GeneralDialog from '../../../../../../shared/general-dialog';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  UserKeys,
  renameSharedDriveDoc,
} from '../../../../../../api/users-api';
import { handleErrors } from '../../../../../../utils/errors';
import { Doc } from '../../../../../../../types';
import { useState } from 'react';
import ChangeLogDialog from '../../../../../../shared/change-log-dialog';
import { useStore } from '../../../../../state/zustand-store';
import { useAnalytics } from '../../../../../../shared/analytics/create-events';
import * as analytics from '../../../../../../shared/analytics/analytics.json';
import {
  DriveRoles,
  isAbleToSeeUIElement,
} from '../../../../../../utils/roles/client-roles';
import { ModuleList } from '../../../../../../shared/analytics/module-list';
import { Messages } from '../../../../../../utils/message-constants';
import ActingAsUserMessage from '../../../../../../shared/acting-as-user-message';

import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';

interface SharedDrivesRenameDialogProps {
  open: boolean;
  handleClose: () => void;
  userEmail: string;
  clickedDoc: Doc;
  selectedFolder: string;
  changeLogRoles: string[];
}

export default function SharedDrivesRenameDialog({
  open,
  handleClose,
  userEmail,
  selectedFolder,
  clickedDoc,
  changeLogRoles,
}: SharedDrivesRenameDialogProps) {
  const { createEvent } = useAnalytics();
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');
  const queryClient = useQueryClient();
  const { setSnackbar } = useStore();
  const [newName, setNewName] = useState(clickedDoc.title);

  const isFolder = clickedDoc.mimeType.includes('folder');

  const url = window.location.href.toLowerCase();
  const isSharedDrives = url.includes('shared');
  const isDriveExplorer = url.includes('explorer');

  const { mutate, isPending } = useMutation({
    mutationFn: () =>
      renameSharedDriveDoc(clickedDoc.id, newName.trim(), changeLogMessage),
    onSuccess: () => {
      setSnackbar({
        open: true,
        severity: 'success',
        message: Messages.Success.Renamed(isFolder ? 'Folder' : 'Document'),
      });

      if (isFolder) {
        queryClient.invalidateQueries({
          queryKey: [UserKeys.SharedDriveFolders, userEmail],
        });

        queryClient.invalidateQueries({
          queryKey: [UserKeys.SharedDriveFolders, userEmail, selectedFolder],
        });
      } else {
        queryClient.invalidateQueries({
          queryKey: [UserKeys.SharedDriveDocuments, userEmail, selectedFolder],
        });
      }

      setChangeLogMessage('');
      handleClose();
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage: Messages.Errors.Network.Renaming(
          isFolder ? 'the folder' : 'the document'
        ),
      });
    },
    onSettled: () => {
      if (isSharedDrives) {
        createEvent(
          analytics.drive.shared_drives.content.rename_drive_content,
          {
            module: ModuleList.modules.drive.name,
            section: ModuleList.modules.drive.sections.sharedDrives,
          }
        );
      } else if (isDriveExplorer) {
        createEvent(
          analytics.drive.drive_explorer.shared_drives.content
            .rename_drive_content,
          {
            module: ModuleList.modules.drive.name,
            section: ModuleList.modules.drive.sections.driveExplorer,
          }
        );
      } else {
        createEvent(
          analytics.user_management.user_details.drive.shared_drives.content
            .rename_drive_content,
          {
            module: ModuleList.modules.userManagement.name,
            section: ModuleList.modules.userManagement.sections.drive,
          }
        );
      }
      setOpenChangeLogDialog(false);
    },
  });

  function handleSubmit() {
    if (isAbleToSeeUIElement(changeLogRoles, DriveRoles.CanRenameDocs.value)) {
      setOpenChangeLogDialog(true);
    } else {
      mutate();
    }
  }

  return (
    <GeneralDialog
      open={open}
      handleClose={() => {
        handleClose();
      }}
      title={`Rename ${isFolder ? 'Folder' : 'Document'}`}
      dialogContent={
        <Box sx={{ px: 4 }}>
          <ActingAsUserMessage userEmail={userEmail} />
          <TextField
            fullWidth
            autoFocus
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
          />
          <ChangeLogDialog
            open={openChangeLogDialog}
            handleClose={() => setOpenChangeLogDialog(false)}
            changeLogMessage={changeLogMessage}
            setChangeLogMessage={setChangeLogMessage}
            mutate={mutate}
            mutateLoading={isPending}
          />
        </Box>
      }
      primaryButton={{
        title: 'Submit',
        action: handleSubmit,
        loading: isPending,
        disabled:
          clickedDoc.title === newName.trim() || newName.trim().length === 0,
      }}
    />
  );
}
